import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { supabase } from '../../lib/supabaseClient';

// Reusing the Chevron styling for consistency
const ChevronIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    className={`w-6 h-6 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path 
      d="M9 18L15 12L9 6" 
      stroke="currentColor" 
      strokeWidth="3" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

export default function Features() {
  const prefersReducedMotion = useReducedMotion();
  // Memoize features array for performance
  const [features, setFeatures] = useState([
    { title: 'Play Sessions', desc: 'Squads, challenges, and casual nights with the community' },
    { title: 'Real Conversations', desc: 'Trends, culture, and life — the stuff people actually talk about' },
    { title: 'Live Interviews', desc: 'Guests, creators, and voices shaping the scene' },
    { title: 'Community Vibes', desc: 'Hangouts that feel like a safe, fun home base' },
    { title: 'Stream Nights', desc: 'Highlights and live shows that bring everyone together' },
    { title: 'Hall of Fame', desc: 'Celebrating the guests who came on the stream' }
  ]);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from('homepage_content').select('*').order('updated_at', { ascending: false }).limit(1);
      if (!error && data?.[0]?.features?.length) setFeatures(data[0].features);
    };
    load();
  }, []);

  // Container animation variants for stagger effect
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08,
        delayChildren: prefersReducedMotion ? 0 : 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 14 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.2 : 0.45,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      id="features"
      className="bg-[#021206] py-32 px-6 overflow-hidden relative"
    >
       {/* Subtle background diagonal element */}
       <div 
         className="absolute top-0 left-0 w-full h-full -z-10 pointer-events-none opacity-50"
         style={{
           background: 'linear-gradient(135deg, transparent 0%, transparent 45%, rgba(161, 255, 79, 0.05) 50%, transparent 55%, transparent 100%)'
         }}
         aria-hidden="true"
       />

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          className="mb-20 space-y-4"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-6">
             <div className="h-1 w-20 bg-[#A1FF4F] transform -skew-x-12" aria-hidden="true" />
             <span className="text-[#A1FF4F] tracking-widest uppercase font-bold text-sm">What We Offer</span>
          </div>

          <h2 className="text-5xl lg:text-7xl font-black uppercase tracking-tight text-white max-w-4xl">
            A Hub for <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A1FF4F] to-green-600">Fun & Community</span>
          </h2>

          <p className="text-xl text-white/70 max-w-2xl mt-6 font-light leading-relaxed">
            Games, interviews, and real conversation in one place.
          </p>
        </motion.div>
        
        {/* Grid Section */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {features.map((feature, idx) => (
          <motion.article
            key={feature.title}
            variants={itemVariants}
            className="group relative bg-[#0F1419]/80 p-10 hover:bg-[#0F1419] transition-all duration-300 focus-within:ring-2 focus-within:ring-[#A1FF4F] focus-within:ring-offset-2 focus-within:ring-offset-[#021206]"
            style={{ clipPath: 'polygon(8% 0, 100% 0, 92% 100%, 0% 100%)' }}
            whileHover={prefersReducedMotion ? undefined : { y: -4 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            tabIndex={0}
            role="article"
            aria-labelledby={`feature-title-${idx}`}
            >
              {/* Hover Accent Line */}
              <div 
                className="absolute top-0 left-0 w-0 h-1 bg-[#A1FF4F] transition-all duration-500 group-hover:w-full group-focus-within:w-full" 
                aria-hidden="true"
              />
              
              {/* Card Content */}
              <div className="flex flex-col h-full justify-between gap-8 relative z-20">
                <div className="flex justify-between items-start">
                   <span 
                     className="text-4xl font-black text-white/10 group-hover:text-[#A1FF4F]/20 transition-colors select-none"
                     aria-hidden="true"
                   >
                     0{idx + 1}
                   </span>
                   <div className="p-2 transform transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 text-[#A1FF4F]">
                     <ChevronIcon />
                   </div>
                </div>

                <div>
                  <h3 
                    id={`feature-title-${idx}`}
                    className="text-2xl font-bold text-white mb-3 uppercase tracking-wide group-hover:text-[#A1FF4F] transition-colors"
                  >
                    {feature.title}
                  </h3>
                  <p className="text-white/60 group-hover:text-white/80 transition-colors font-light leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </div>

              {/* Decorative corner glare */}
              <div 
                className="absolute -top-10 -right-10 w-20 h-20 bg-white/5 rotate-45 blur-xl group-hover:bg-[#A1FF4F]/20 transition-all duration-500 pointer-events-none" 
                aria-hidden="true"
              />
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
